import React, { useState, useEffect, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../context/CartContext";
import { useUser } from "../../../context/UserContext";
import { useStoreSettings } from "../../../hooks/useStoreSettings";
import "./Navbar.css";
import CartPanel from "./CartPanel/CartPanel";
import SideMenu from "./SideMenu/SideMenu";
import SearchOverlay from "./SearchOverlay/SearchOverlay";
import UserDropdown from "./UserDropDown/UserDropdown";
import { Search, ShoppingCart, Heart, User } from "lucide-react";

// Destructure isScrolled from props
const Navbar = forwardRef(({ isScrolled }, ref) => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const { storeSettings } = useStoreSettings();
  const [localItemCount, setLocalItemCount] = useState(0);
  const [cartAnimation, setCartAnimation] = useState(false);

  // State for navbar behavior
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { itemCount, cartOpen, cartVisible, refreshCart, openCart, closeCart } =
    useCart();

  // Update cart counter and animation
  useEffect(() => {
    if (itemCount > 0) {
      setCartAnimation(true);
      setLocalItemCount(itemCount);
      setTimeout(() => setCartAnimation(false), 300);
    } else {
      setLocalItemCount(0);
    }
  }, [itemCount]);

  // Refresh cart data periodically or on focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshCart();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const refreshInterval = setInterval(() => {
      refreshCart();
    }, 10000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, [refreshCart]);

  // Watch for compact class changes from MainLayout
  useEffect(() => {
    const checkCompactState = () => {
      if (ref.current) {
        const hasCompact = ref.current.classList.contains("compact");
        setIsCompact(hasCompact);
      }
    };

    // Check initially
    checkCompactState();

    // Set up a mutation observer to watch for class changes
    const observer = new MutationObserver(checkCompactState);
    if (ref.current) {
      observer.observe(ref.current, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => observer.disconnect();
  }, [ref]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Navigation functions
  const goToHome = () => {
    navigate("/");
    setMenuOpen(false);
  };

  // Cart functions
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  return (
    <>
      <nav
        className={`navbar ${isScrolled ? "scrolled" : ""}`}
        ref={ref}
        style={{
          backgroundColor: isScrolled ? "#ffffff" : "transparent",
        }}
      >
        {/* Left side: Hamburger + Search */}
        <div className="menu-search-container" aria-hidden={!isCompact}>
          {/* Hamburger */}
          <div
            className={`hamburger-button ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </div>

          {/* Search icon */}
          <div
            className="nav-icon search-icon no-flip"
            onClick={() => setSearchOpen(true)}
            aria-label="Open search"
          >
            <Search className="icon" />
          </div>
        </div>

        {/* Center: Logo */}
        <div className="logo" onClick={goToHome} style={{ cursor: "pointer" }}>
          <img
            src={storeSettings?.logoUrl || "/placeholder.png"}
            alt="Store Logo"
            onError={(e) => {
              // Fallback to hardcoded logo if database logo fails to load
              e.target.src = "/placeholder.png";
            }}
          />
        </div>

        {/* Right side: User, Wishlist, Cart */}
        <div className="nav-icons" aria-hidden={!isCompact}>
          <div className="desktop-only-icons">
            {/* ✅ User icon toggles dropdown */}
            <div
              className="nav-icon"
              onClick={() => setUserDropdownOpen(true)}
              aria-label="User account"
            >
              <User className="icon" />
            </div>
            {currentUser && (
              <div
                className="nav-icon"
                onClick={() => navigate("/wishlist")}
                aria-label="Wishlist"
              >
                <Heart className="icon" />
              </div>
            )}
          </div>

          <div
            className={`nav-icon cart-icon-container ${
              cartAnimation ? "cart-update-animation" : ""
            }`}
          >
            <div
              className="cart-icon-wrapper"
              onClick={openCart}
              aria-label="Shopping cart"
            >
              <ShoppingCart
                className="cart-icon-svg"
                size={14}
                strokeWidth={1.5}
              />
              {localItemCount > 0 && (
                <span className="cart-count">{localItemCount}</span>
              )}
            </div>
          </div>
        </div>
      </nav>
      <UserDropdown
        open={userDropdownOpen}
        onClose={() => setUserDropdownOpen(false)}
      />
      {userDropdownOpen && (
        <div
          className="dropdown-overlay"
          onClick={() => setUserDropdownOpen(false)}
        />
      )}

      {/* Cart Panel */}
      {cartVisible && (
        <>
          <div
            className={`cart-overlay ${cartOpen ? "fade-in" : "fade-out"}`}
            onClick={closeCart}
          />
          <CartPanel
            className={cartOpen ? "slide-in" : "slide-out"}
            isVisible={cartOpen}
          />
        </>
      )}

      {/* Search Overlay Component */}
      <SearchOverlay searchOpen={searchOpen} setSearchOpen={setSearchOpen} />

      {/* Side Menu Component */}
      <SideMenu
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        isMobile={isMobile}
      />
    </>
  );
});
export default Navbar;
